import React, { useEffect } from "react";
import { Typography, Divider, Tag } from "antd";
import { Link, useLocation } from "react-router-dom";
import { useContactModal } from "../context/ContactModalContext";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useTranslation } from "../i18n/useTranslation";
import {
  Database,
  Shield,
  Clock,
  BarChart3,
  Cpu,
  Network,
  Brain,
  Zap,
  Factory,
  Leaf,
  HeartPulse,
  GraduationCap,
  ShoppingBag,
  Truck,
  Home,
  Building2,
} from "lucide-react";

const { Title, Text, Paragraph } = Typography;

const Solutions = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { openContactModal } = useContactModal();

  useEffect(() => {
    if (location.hash === "#smart-solutions") {
      const timer = setTimeout(() => {
        const el = document.getElementById("smart-solutions");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  // 解决方案数据
  const solutions = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: t("solutionsSolution1Title"),
      description: t("solutionsSolution1Desc"),
      features: [t("solutionsSolution1Feature1"), t("solutionsSolution1Feature2")],
      image: require("../assets/img/automation_platform.jpg"),
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: t("solutionsSolution2Title"),
      description: t("solutionsSolution2Desc"),
      features: [t("solutionsSolution2Feature1"), t("solutionsSolution2Feature2"), t("solutionsSolution2Feature3")],
      image: require("../assets/img/embodied_robot.jpg"),
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: t("solutionsSolution3Title"),
      description: t("solutionsSolution3Desc"),
      features: [t("solutionsSolution3Feature1"), t("solutionsSolution3Feature2"), t("solutionsSolution3Feature3"), t("solutionsSolution3Feature4")],
      image: require("../assets/img/electronic_toys.jpg"),
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t("solutionsSolution4Title"),
      description: t("solutionsSolution4Desc"),
      features: [t("solutionsSolution4Feature1"), t("solutionsSolution4Feature2"), t("solutionsSolution4Feature3")],
      image: require("../assets/img/automonous_vehicle.webp"),
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t("solutionsSolution5Title"),
      description: t("solutionsSolution5Desc"),
      features: [t("solutionsSolution5Feature1"), t("solutionsSolution5Feature2"), t("solutionsSolution5Feature3")],
      image: require("../assets/img/unmanned_aerial_vehicle.webp"),
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: t("solutionsSolution6Title"),
      description: t("solutionsSolution6Desc"),
      features: [
        t("solutionsSolution6Feature1"),
        t("solutionsSolution6Feature2"),
        t("solutionsSolution6Feature3"),
        t("solutionsSolution6Feature4"),
      ],
      image: require("../assets/img/robots_arm.png"),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <Title level={1} className="mb-4">
                {t("solutionsHeroTitle")}
              </Title>
              <div className="text-gray-600 py-6 text-base">
                {t("solutionsHeroDesc")}
              </div>
              {/* <div className="flex gap-4 mt-2 mb-6">
                <Button variant="blue-griadient" size="lg">
                  了解更多
                </Button>
                <Button variant="secondary" size="lg">
                  联系我们
                </Button>
              </div> */}
            </div>
            <div className="md:w-1/2 max-w-[560px]">
              <img src={require("../assets/img/solution_hero.webp")} />
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Overview - 为各行业提供智能解决方案 */}
      <section
        id="smart-solutions"
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              {t("solutionsOverviewTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("solutionsOverviewTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("solutionsOverviewDesc")}
            </p>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                    {solution.icon}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-slate-800">
                    {solution.title}
                  </h3>
                  <p className="mb-4 text-slate-600 text-sm">
                    {solution.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {solution.features.map((feature, idx) => (
                      <Tag key={idx} color="blue" className="text-xs">
                        {feature}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Advantages */}
      <section className="bg-gray-100 py-24">
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              {t("solutionsTechAdvantagesTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("solutionsTechAdvantagesTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("solutionsTechAdvantagesDesc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                {t("solutionsTechAdvantage1Title")}
              </h3>
              <p className="text-slate-600 text-sm">
                {t("solutionsTechAdvantage1Desc")}
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                {t("solutionsTechAdvantage2Title")}
              </h3>
              <p className="text-slate-600 text-sm">
                {t("solutionsTechAdvantage2Desc")}
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Network className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                {t("solutionsTechAdvantage3Title")}
              </h3>
              <p className="text-slate-600 text-sm">
                {t("solutionsTechAdvantage3Desc")}
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                {t("solutionsTechAdvantage4Title")}
              </h3>
              <p className="text-slate-600 text-sm">
                {t("solutionsTechAdvantage4Desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Areas */}
      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              {t("solutionsApplicationAreasTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("solutionsApplicationAreasTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("solutionsApplicationAreasDesc")}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                name: t("solutionsIndustry1"),
                icon: <Factory className="w-5 h-5" />,
                gradient: "from-blue-500 to-cyan-500",
              },
              {
                name: t("solutionsIndustry2"),
                icon: <Leaf className="w-5 h-5" />,
                gradient: "from-green-500 to-emerald-500",
              },
              {
                name: t("solutionsIndustry3"),
                icon: <HeartPulse className="w-5 h-5" />,
                gradient: "from-red-500 to-pink-500",
              },
              {
                name: t("solutionsIndustry4"),
                icon: <GraduationCap className="w-5 h-5" />,
                gradient: "from-purple-500 to-indigo-500",
              },
              {
                name: t("solutionsIndustry5"),
                icon: <ShoppingBag className="w-5 h-5" />,
                gradient: "from-yellow-500 to-amber-500",
              },
              {
                name: t("solutionsIndustry6"),
                icon: <Truck className="w-5 h-5" />,
                gradient: "from-orange-500 to-red-500",
              },
              {
                name: t("solutionsIndustry7"),
                icon: <Home className="w-5 h-5" />,
                gradient: "from-teal-500 to-cyan-500",
              },
              {
                name: t("solutionsIndustry8"),
                icon: <Building2 className="w-5 h-5" />,
                gradient: "from-blue-600 to-indigo-600",
              },
            ].map((industry, index) => (
              <div
                key={index}
                className="group relative p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                <div
                  className="`absolute inset-0 bg-gradient-to-br opacity-5 group-hover:opacity-10 transition-opacity duration-300 ${industry.gradient}`"
                  style={{
                    backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                  }}
                ></div>
                <div
                  className={`inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-gradient-to-br rounded-lg shadow-lg ${industry.gradient}`}
                >
                  {industry.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2 relative z-10">
                  {industry.name}
                </h3>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${industry.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="bg-gray-100 py-24 md:pb-32">
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("solutionsCaseStudiesTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500 mb-12">
              {t("solutionsCaseStudiesDesc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: t("solutionsCase1Title"),
                description: t("solutionsCase1Desc"),
                client: t("solutionsCase1Client"),
              },
              {
                title: t("solutionsCase2Title"),
                description: t("solutionsCase2Desc"),
                client: t("solutionsCase2Client"),
              },
              {
                title: t("solutionsCase3Title"),
                description: t("solutionsCase3Desc"),
                client: t("solutionsCase3Client"),
              },
            ].map((caseStudy, index) => (
              <div
                key={index}
                className="p-8 bg-gray-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="mb-4 text-xl font-bold text-slate-800">{caseStudy.title}</h3>
                <p className="text-base text-slate-600 mb-4">
                  {caseStudy.description}
                </p>
                <p className="text-sm text-slate-500">{caseStudy.client}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row items-center p-8 md:p-12 gap-8">
              <div className="md:w-3/5">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {t("solutionsCTATitle")}
                </h2>
                <p className="text-blue-100 mb-6">
                  {t("solutionsCTADesc")}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="bg-white text-blue-600 hover:bg-blue-50"
                    onClick={openContactModal}
                  >
                    {t("solutionsCTAButton")}
                  </Button>
                </div>
              </div>
              <div className="md:w-2/5">
                <img
                  src={require("../assets/img/girl_robot_v_1.webp")}
                  alt="智能机器人"
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Solutions;
